import { GET_ASSET_QUERY_KEY } from '@/hooks/api/assets/useGetAsset';
import { LIST_FOLDER_CONTENTS_QUERY_KEY } from '@/hooks/api/folders/useFetchFolderContents';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { CROSS_TENANT_ASSETS_GEOMETRIC_SEARCH_QUERY_KEY } from '../crossTenant/assetsGeometricSearch/query';
import { CROSS_TENANT_ASSETS_PART_SEARCH_QUERY_KEY } from '../crossTenant/assetsPartSearch/query';
import { CROSS_TENANT_ASSETS_VISUAL_SEARCH_QUERY_KEY } from '../crossTenant/assetsVisualSearch/query';
import { CROSS_TENANT_TEXT_SEARCH_QUERY_KEY } from '../crossTenant/textsearch/query';
import { CROSS_TENANT_VISUAL_SEARCH_QUERY_KEY } from '../crossTenant/visualSearch/query';

export const useDeleteMetadataFromAsset = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: string) => void;
} = {}) => {
  const queryClient = useQueryClient();
  const { getTenantId } = useTenants();
  const { mutate, isPending, error } = useMutation({
    mutationFn: ({ assetId, metadataFieldNames }: { assetId: string; metadataFieldNames: string[] }) =>
      api.assets.deleteMetadataFromAsset(getTenantId(), assetId, { metadataFieldNames }),
    onSuccess: (_data, { assetId }) => {
      queryClient.resetQueries({
        queryKey: [LIST_FOLDER_CONTENTS_QUERY_KEY],
        exact: false,
      });
      queryClient.resetQueries({ queryKey: [GET_ASSET_QUERY_KEY, { assetId }] });
      queryClient.resetQueries({ queryKey: [CROSS_TENANT_TEXT_SEARCH_QUERY_KEY], exact: false });
      queryClient.resetQueries({ queryKey: [CROSS_TENANT_VISUAL_SEARCH_QUERY_KEY], exact: false });
      queryClient.resetQueries({ queryKey: [CROSS_TENANT_ASSETS_GEOMETRIC_SEARCH_QUERY_KEY], exact: false });
      queryClient.resetQueries({ queryKey: [CROSS_TENANT_ASSETS_PART_SEARCH_QUERY_KEY], exact: false });
      queryClient.resetQueries({ queryKey: [CROSS_TENANT_ASSETS_VISUAL_SEARCH_QUERY_KEY], exact: false });
      onSuccess?.();
    },
    onError: (error: AxiosError<{ message: string }>) => {
      onError?.(error.response?.data.message ?? 'Failed to delete metadata from asset');
    },
  });

  return {
    mutate,
    isPending,
    error,
  };
};
